import { useEffect, useRef, useState } from 'react';
import {
  useAiProvidersHealth,
  type AiProvidersSource,
  type HistoryPoint,
  type ProviderHealth,
  type ProviderName,
} from './useAiProvidersHealth';

/**
 * AiProvidersChip — multi-provider AI-health indicator (30 May 2026; re-homed
 * to portal-shared 8 Jun 2026, ADR-055 follow-up).
 *
 * Replaces the single-provider AnthropicHealthChip. Compact closed-state chip
 * in the navbar: three coloured dots (per-provider latency band) + "AI · N"
 * label + caret. Click opens a dropdown listing each provider with favicon +
 * latency, latency-coloured (green/amber/red) or grey "—" when the provider
 * isn't pingable.
 *
 * Design lineage: ai-providers-chip-mockup.html (consumer-friendly — shows to
 * staff AND subscribers, so no technical copy, no endpoint URLs, no
 * "Not configured" labels — only the dash treatment). This is exactly why the
 * chip is now shared: ADR-034 §5 always intended it for both audiences.
 *
 * The data SOURCE is injected (`source` prop) — admin/staff reads admin-api,
 * subscriber reads portal-api; both ultimately serve the same DDB probe table
 * (ADR-041). The chip is source-agnostic.
 *
 * Closes on outside-click + Esc. Hover or focus opens after 0ms (click also
 * works). Dropdown is sticky once opened — operator reads at their own pace.
 *
 * Latency graph (1 Jun 2026): the dropdown footer is a live inline SVG graph
 * plotting the last 60 minutes of per-provider latency. A "?" toggle switches
 * to the explainer + RAG legend. Graph lines use fixed identity colours
 * (blue / purple / silver) intentionally distinct from the RAG status colours
 * (green/amber/red) — the lines identify providers, not their current status.
 */

interface ProviderMeta {
  label: string;
  faviconDomain: string;
}

const PROVIDER_META: Record<ProviderName, ProviderMeta> = {
  anthropic: { label: 'Anthropic',  faviconDomain: 'anthropic.com' },
  openai:    { label: 'OpenAI',     faviconDomain: 'openai.com' },
  xai:       { label: 'xAI',        faviconDomain: 'x.ai' },
};

/**
 * Identity colours for graph lines — must NOT match the RAG status colours
 * (green = online, amber = slow, red = offline), otherwise the graph would
 * appear to chart status rather than identify providers.
 */
const GRAPH_LINE_COLORS: Record<ProviderName, string> = {
  anthropic: '#60a5fa',  // blue   — provider identity
  openai:    '#c084fc',  // purple — provider identity
  xai:       '#e2e8f0',  // silver — provider identity (neutral; avoids all RAG zone colours)
};

// Latency bands tuned for AP datacentre (Sydney) → US-hosted providers
// (30 May 2026). Cross-Pacific RTT + TLS handshake + provider-side API
// processing typically lands a healthy ping in the 250-500ms range —
// nothing actionable in there. Bands chosen so the chip only flips
// colour when something genuinely degraded happens.
const SLOW_LATENCY_MS = 300;
const VERY_SLOW_LATENCY_MS = 1000;

/** Fixed Y-axis ceiling for the latency graph (ms). */
const GRAPH_Y_MAX = 1200;

type LatencyBand = 'online' | 'slow' | 'offline' | 'inactive';
type DropdownView = 'graph' | 'help';

// A reply with a measured round trip gets the normal bars whatever the reply said — including a
// rejected key (401), which the server reported as `not-configured` before 1 November 2026
// (ADR-034 Amendment 2, quality register Q27: "that was the original design"). Only a provider
// with NO round trip — a key never provisioned, so no request was sent — shows the grey dash.
export function latencyBand(p: ProviderHealth): LatencyBand {
  if (p.status === 'loading') return 'inactive';
  if (p.status === 'offline') return 'offline';
  if (p.latencyMs == null) return 'inactive';
  if (p.latencyMs > VERY_SLOW_LATENCY_MS) return 'offline';
  if (p.latencyMs > SLOW_LATENCY_MS) return 'slow';
  return 'online';
}

export function latencyText(p: ProviderHealth): string {
  const band = latencyBand(p);
  if (band === 'inactive' || band === 'offline') return '—';
  return `${p.latencyMs} ms`;
}

// Hover open/close timings — match the .portal-tooltip-anchor pattern
// used elsewhere (Soft delete / Hard delete / Request Info tooltips).
// 150ms show delay prevents the dropdown from flashing on quick mouse-
// pass-overs; 300ms hide delay lets the operator's mouse cross the
// gap between chip and dropdown without losing the popover.
const HOVER_SHOW_DELAY_MS = 150;
const HOVER_HIDE_DELAY_MS = 300;

// ---------------------------------------------------------------------------
// Latency graph — inline SVG, no charting library
// ---------------------------------------------------------------------------

/** SVG layout constants (viewBox units). */
const G = {
  W: 260, H: 145,
  PL: 32, PR: 6, PT: 5, PB: 16,
} as const;
const G_PW = G.W - G.PL - G.PR;
const G_PH = G.H - G.PT - G.PB;

/** Graph window in ms — the visible X span (60 minutes). */
const GRAPH_WINDOW_MS = 60 * 60 * 1000;

/**
 * The X-axis labels, placed by MINUTES AGO across the plot width.
 *
 * ⚠ QUALITY REGISTER Q15 (30 October 2026). These used to be placed with
 * `gXp(i, 360)` — the data-point function, which takes a wall-clock timestamp and
 * "now" in milliseconds — passing a sample INDEX (0…359) as the timestamp and 360 as
 * "now". Every label's "age" was under 360 ms of a 60-minute window, so all five
 * landed within a pixel of the right edge and drew on top of each other: the garbled
 * "-60m" in the bottom-right corner of every chip. The ends are anchored inward
 * (start / end) so "−60m" and "now" sit inside the plot instead of being half clipped.
 * Overlap is measured in real Chrome by `scripts/probe-ai-chip-axis-labels.mjs`.
 */
const GRAPH_X_LABELS: ReadonlyArray<{ label: string; x: number; anchor: 'start' | 'middle' | 'end' }> = [
  { label: '−60m', minutesAgo: 60 },
  { label: '−45m', minutesAgo: 45 },
  { label: '−30m', minutesAgo: 30 },
  { label: '−15m', minutesAgo: 15 },
  { label: 'now', minutesAgo: 0 },
].map(({ label, minutesAgo }) => ({
  label,
  x: G.PL + ((60 - minutesAgo) / 60) * G_PW,
  anchor: minutesAgo === 60 ? 'start' : minutesAgo === 0 ? 'end' : 'middle',
}));

function gYp(ms: number): number {
  return G.PT + G_PH * (1 - Math.min(ms, GRAPH_Y_MAX) / GRAPH_Y_MAX);
}

/**
 * X position for a data point based on its actual wall-clock timestamp.
 * Points older than GRAPH_WINDOW_MS before `now` are clamped to the left
 * edge; `now` maps to the right edge. Gaps during hibernation render as
 * genuine empty space rather than compressed data.
 */
function gXp(t: number, now: number): number {
  const age = Math.min(now - t, GRAPH_WINDOW_MS);
  return G.PL + ((GRAPH_WINDOW_MS - age) / GRAPH_WINDOW_MS) * G_PW;
}

/** Split a history buffer into polyline point-strings, breaking at nulls.
 *  Skips points outside the visible window. */
function historySegments(points: HistoryPoint[], now: number): string[][] {
  const windowStart = now - GRAPH_WINDOW_MS;
  const segments: string[][] = [];
  let current: string[] = [];
  points.forEach((pt) => {
    if (pt.t < windowStart || pt.latencyMs === null) {
      if (current.length > 1) segments.push(current);
      current = [];
    } else {
      current.push(
        `${gXp(pt.t, now).toFixed(1)},${gYp(pt.latencyMs).toFixed(1)}`,
      );
    }
  });
  if (current.length > 1) segments.push(current);
  return segments;
}

interface LatencyGraphProps {
  history: Record<ProviderName, HistoryPoint[]>;
  providers: ProviderHealth[];
}

function LatencyGraph({ history, providers }: LatencyGraphProps) {
  // Compute once per render so all data points share the same "now".
  const now   = Date.now();
  const y300  = gYp(SLOW_LATENCY_MS);
  const y1000 = gYp(VERY_SLOW_LATENCY_MS);
  const y0    = gYp(0);
  const yTop  = G.PT;

  // Screen-reader summary: current latency per provider.
  const srLabel = [
    'Latency history — last 60 minutes.',
    ...providers.map(
      (p) =>
        `${PROVIDER_META[p.provider].label}: ${
          p.latencyMs != null ? `${p.latencyMs} ms` : 'no signal'
        }.`,
    ),
  ].join(' ');


  return (
    <span className="portal-ai-dropdown-graph-wrap">
      <svg
        className="portal-ai-dropdown-graph-svg"
        viewBox={`0 0 ${G.W} ${G.H}`}
        role="img"
        aria-label={srLabel}
      >
        {/* RAG band fills */}
        <rect x={G.PL} y={y300}  width={G_PW} height={y0 - y300}    fill="#4ade80" opacity="0.05" />
        <rect x={G.PL} y={y1000} width={G_PW} height={y300 - y1000} fill="#fbbf24" opacity="0.05" />
        <rect x={G.PL} y={yTop}  width={G_PW} height={y1000 - yTop} fill="#ef4444" opacity="0.04" />

        {/* Horizontal grid + Y-axis labels */}
        {([0, 300, 600, 900, 1200] as const).map((ms) => {
          const y = gYp(ms);
          return (
            <g key={ms}>
              <line x1={G.PL} y1={y} x2={G.W - G.PR} y2={y} stroke="#1e3a5f" strokeWidth="0.5" />
              <text
                x={G.PL - 3}
                y={y + 3.5}
                textAnchor="end"
                fontSize="9"
                fill="#475569"
              >
                {ms >= 1000 ? `${ms / 1000}k` : ms}
              </text>
            </g>
          );
        })}

        {/* RAG threshold dashed lines */}
        <line x1={G.PL} y1={y300}  x2={G.W - G.PR} y2={y300}  stroke="#4ade80" strokeWidth="1" strokeDasharray="3 2" opacity="0.45" />
        <line x1={G.PL} y1={y1000} x2={G.W - G.PR} y2={y1000} stroke="#ef4444" strokeWidth="1" strokeDasharray="3 2" opacity="0.45" />

        {/* X-axis time labels — see GRAPH_X_LABELS for why the ends are anchored inward. */}
        {GRAPH_X_LABELS.map(({ label, x, anchor }) => (
          <text
            key={label}
            className="portal-ai-graph-xlabel"
            x={x.toFixed(1)}
            y={G.H - 3}
            textAnchor={anchor}
            fontSize="9"
            fill="#475569"
          >
            {label}
          </text>
        ))}

        {/* Y-axis rule */}
        <line x1={G.PL} y1={G.PT} x2={G.PL} y2={G.PT + G_PH} stroke="#334155" strokeWidth="0.5" />

        {/* Provider polylines */}
        {PROVIDER_ORDER_RENDER.map((name) => {
          const pts = history[name];
          if (!pts.length) return null;
          const color = GRAPH_LINE_COLORS[name];
          return historySegments(pts, now).map((seg, si) => (
            <polyline
              key={`${name}-${si}`}
              points={seg.join(' ')}
              fill="none"
              stroke={color}
              strokeWidth="1.5"
              strokeLinejoin="round"
              opacity="0.9"
            />
          ));
        })}

        {/* End-point dots — latest value per provider (within window) */}
        {PROVIDER_ORDER_RENDER.map((name) => {
          const pts  = history[name];
          const windowStart = now - GRAPH_WINDOW_MS;
          // Find the most recent point within the visible window.
          const last = [...pts].reverse().find(
            (p) => p.t >= windowStart && p.latencyMs !== null,
          );
          if (!last || last.latencyMs === null) return null;
          return (
            <circle
              key={name}
              cx={gXp(last.t, now).toFixed(1)}
              cy={gYp(last.latencyMs).toFixed(1)}
              r="2.5"
              fill={GRAPH_LINE_COLORS[name]}
            />
          );
        })}
      </svg>
    </span>
  );
}

/** Render order for graph lines (back to front — xAI behind, Anthropic on top). */
const PROVIDER_ORDER_RENDER: ProviderName[] = ['xai', 'openai', 'anthropic'];

// ---------------------------------------------------------------------------
// Main chip component
// ---------------------------------------------------------------------------

export interface AiProvidersChipProps {
  /** Per-portal data source (admin-api for staff, portal-api for subscriber). */
  source: AiProvidersSource;
}

export function AiProvidersChip({ source }: AiProvidersChipProps) {
  const { providers, history } = useAiProvidersHealth(source);
  const [open, setOpen] = useState(false);
  const [dropdownView, setDropdownView] = useState<DropdownView>('graph');
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset to graph view whenever the dropdown closes.
  useEffect(() => {
    if (!open) setDropdownView('graph');
  }, [open]);

  function scheduleShow(): void {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (showTimerRef.current) return;
    showTimerRef.current = setTimeout(() => {
      setOpen(true);
      showTimerRef.current = null;
    }, HOVER_SHOW_DELAY_MS);
  }
  function scheduleHide(): void {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) return;
    hideTimerRef.current = setTimeout(() => {
      setOpen(false);
      hideTimerRef.current = null;
    }, HOVER_HIDE_DELAY_MS);
  }

  return (
    <span
      className="portal-ai-chip-wrap"
      onMouseEnter={scheduleShow}
      onMouseLeave={scheduleHide}
      onFocus={scheduleShow}
      onBlur={scheduleHide}
    >
      <button
        type="button"
        className="portal-ai-chip"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="AI providers status"
      >
        <span className="portal-ai-chip-dots" aria-hidden="true">
          {providers.map((p) => (
            <span
              key={p.provider}
              className={`portal-ai-chip-dot portal-ai-chip-dot-${latencyBand(p)}`}
            />
          ))}
        </span>
        <span className="portal-ai-chip-label">AI · {providers.length}</span>
        <svg className="portal-ai-chip-caret" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <polyline points="3,4.5 6,7.5 9,4.5" />
        </svg>
      </button>
      {open && (
        <span className="portal-ai-dropdown" role="dialog" aria-label="AI providers">
          <span className="portal-ai-dropdown-header">AI providers</span>
          {providers.map((p) => {
            const meta = PROVIDER_META[p.provider];
            const band = latencyBand(p);
            return (
              <span key={p.provider} className="portal-ai-dropdown-row">
                <span className="portal-ai-dropdown-favicon">
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${meta.faviconDomain}&sz=64`}
                    alt=""
                    width={20}
                    height={20}
                  />
                </span>
                <span className="portal-ai-dropdown-provider">{meta.label}</span>
                <span className={`portal-ai-dropdown-latency portal-ai-dropdown-latency-${band}`}>
                  <span className={`portal-ai-chip-dot portal-ai-chip-dot-${band}`} aria-hidden="true" />
                  {latencyText(p)}
                </span>
              </span>
            );
          })}

          {/* Graph / help section — replaces the static explainer footer. */}
          <span className="portal-ai-dropdown-graph-section">

            {/* Header row: "Last 60 min" + legend + ? toggle */}
            <span className="portal-ai-dropdown-graph-header">
              <span className="portal-ai-dropdown-graph-title">Last 60 min</span>
              <span className="portal-ai-dropdown-graph-legend" aria-hidden="true">
                {providers.map((p) => (
                  <span key={p.provider} className="portal-ai-dropdown-graph-legend-item">
                    <span
                      className="portal-ai-dropdown-graph-legend-line"
                      style={{ background: GRAPH_LINE_COLORS[p.provider] }}
                    />
                    {PROVIDER_META[p.provider].label}
                  </span>
                ))}
              </span>
              <button
                type="button"
                className={`portal-ai-dropdown-graph-toggle${dropdownView === 'help' ? ' active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownView((v) => (v === 'graph' ? 'help' : 'graph'));
                }}
                aria-pressed={dropdownView === 'help'}
                aria-label={dropdownView === 'graph' ? 'Show help' : 'Show graph'}
              >
                ?
              </button>
            </span>

            {dropdownView === 'graph' && (
              <LatencyGraph history={history} providers={providers} />
            )}

            {dropdownView === 'help' && (
              <span className="portal-ai-dropdown-footer">
                <span className="portal-ai-dropdown-footer-row">
                  <strong>What this shows:</strong> round-trip time from our
                  Sydney servers to each provider's API. Probed every 10
                  seconds server-side — history persists across page reloads
                  and Mac hibernation.
                </span>
                <span className="portal-ai-dropdown-footer-bands">
                  <span className="portal-ai-dropdown-footer-band">
                    <span className="portal-ai-chip-dot portal-ai-chip-dot-online" aria-hidden="true" />
                    ≤ 300 ms
                  </span>
                  <span className="portal-ai-dropdown-footer-band">
                    <span className="portal-ai-chip-dot portal-ai-chip-dot-slow" aria-hidden="true" />
                    ≤ 1000 ms
                  </span>
                  <span className="portal-ai-dropdown-footer-band">
                    <span className="portal-ai-chip-dot portal-ai-chip-dot-offline" aria-hidden="true" />
                    &gt; 1000 ms / unreachable
                  </span>
                  <span className="portal-ai-dropdown-footer-band">
                    <span className="portal-ai-chip-dot portal-ai-chip-dot-inactive" aria-hidden="true" />
                    no signal
                  </span>
                </span>
              </span>
            )}

          </span>
        </span>
      )}
    </span>
  );
}
